import React from 'react';
import { Heart, ShieldCheck, Utensils, Award, ExternalLink } from 'lucide-react';

const GlutenFree = () => {
  const features = [
    {
      icon: ShieldCheck,
      title: 'Certificazione AIC',
      description: 'Locale certificato dall\'Associazione Italiana Celiachia',
    },
    {
      icon: Utensils,
      title: 'Ambienti Separati',
      description: 'Due cucine dedicate per evitare contaminazioni',
    },
    {
      icon: Heart,
      title: 'Ampia Scelta',
      description: 'Tutti i nostri piatti disponibili anche senza glutine',
    },
    {
      icon: Award,
      title: 'Eccellenza',
      description: 'Qualità e sicurezza garantite al 100%',
    },
  ];

  return (
    <section id="senza-glutine" className="py-24 bg-gradient-to-br from-blue-50 to-indigo-50">
      <div className="container mx-auto px-6 lg:px-12">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-6 py-2 bg-blue-100 rounded-full mb-4">
              <Heart className="text-blue-600" size={20} />
              <span className="text-blue-800 font-semibold uppercase tracking-wide text-sm">100% Senza Glutine</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
              Menù Senza Glutine
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Il nostro locale è certificato AIC e offre una vastissima scelta di piatti gluten free,
              preparati con la massima attenzione per garantire la vostra sicurezza
            </p>
            <div className="w-24 h-1 bg-blue-600 mx-auto rounded-full mt-6"></div>
          </div>

          {/* Features Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 text-center"
              >
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <feature.icon className="text-blue-600" size={32} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>

          {/* Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left - Image */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_grano-v2/artifacts/jfwy7xir_iCM8Z8q9mV-oVVyLc0BhyMoaXiTfIgAEes8CInFZZlkeJxFPc.jpg"
                  alt="Pasta Senza Glutine Grano e Sale"
                  className="w-full h-96 object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-blue-900/60 to-transparent"></div>
                <div className="absolute bottom-6 left-6 right-6">
                  <div className="flex items-center space-x-2 text-white">
                    <Heart className="fill-white" size={24} />
                    <span className="text-lg font-bold">100% Certificato AIC</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right - Content */}
            <div className="space-y-6">
              <h3 className="text-3xl font-bold text-gray-900" style={{ fontFamily: 'Bebas Neue, sans-serif' }}>
                Un'Attenzione Speciale per Chi Ha Esigenze Particolari
              </h3>

              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Pizza senza glutine</strong> con impasto certificato, disponibile in tutti i gusti del menu
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Pasta fresca senza glutine</strong> preparata quotidianamente
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Dolci senza glutine</strong> artigianali, belli esteticamente e buonissimi
                  </p>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-blue-600 rounded-full mt-2 flex-shrink-0"></div>
                  <p className="text-gray-700 leading-relaxed">
                    <strong>Ambiente separato</strong> con cucina dedicata per evitare ogni contaminazione
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 rounded-xl p-6 border-l-4 border-blue-600">
                <p className="text-gray-700 leading-relaxed italic">
                  "Estrema attenzione per il senza glutine, qualità del cibo molto alta con vastissima scelta dal menù, dolci compresi."
                </p>
                <p className="text-sm text-gray-600 mt-2">— Jessica C., Cliente Affezionata</p>
              </div>

              <a
                href="https://f9vvvv5kmoeqspr7aqgpyobkuwn6tcl1.pages.pienissimo.pro/home?name=%F0%9F%93%9A"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center space-x-2 px-8 py-4 bg-blue-600 text-white rounded-full font-semibold hover:bg-blue-700 transition-all duration-300 hover:scale-105 shadow-lg"
              >
                <span>Scopri il Menù Senza Glutine</span>
                <ExternalLink size={18} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GlutenFree;
